const isBlank = /^[\r\n\s]{0,}$/

const defaultConfig = {
  wordsPerLine: 33,
  linesPerInch: 7,
  ignoreEmptyElements: true,
  validElements: ['text'],
}

/**
 * estimates word/line/inch count for a given number of content elements, this is not exact
 * the configuration is client-specific, it does not take into account word wrap,
 * which is why words per line is used, and not chars per line
 *
 * @param {object|array} elements - content elements, one or many
 * @param {object} config - client specific config
 *   @prop {number} wordsPerLine
 *   @prop {number} linesPerInch
 *   @prop {boolean} ignoreEmptyElements - if false, empty elements will count towards line count but not word count
 *   @prop {array<string>} validElements - list of valid content elements that count towards count
 * @return {object}
 *   @prop {number} - chars
 *   @prop {number} - words
 *   @prop {number} - lines
 *   @prop {number} - inches
 */
export default function getWordCount(elements = [], conf = defaultConfig) {
  const config = { ...defaultConfig, ...conf }
  return (Array.isArray(elements) ? elements : [elements]).reduce(
    (acc, el) => {
      if (config.validElements.indexOf(el.type) < 0) {
        return acc
      }
      // if we ever have to do this for lists, this is why we are doing a nested reduce
      const { chars, words, lines } = (Array.isArray(el.items)
        ? el.items
        : [el]
      ).reduce(
        (acc2, { content = null }) => {
          if (content === null) {
            return acc2
          }
          const isEmpty = isBlank.test(content)
          const ignoreContent = config.ignoreEmptyElements === true
          if (ignoreContent && isEmpty === true) {
            return acc2
          } else if (
            (ignoreContent && isEmpty === false) ||
            (!ignoreContent && isEmpty === false)
          ) {
            const numWords = content.length ? content.split(' ').length : 0
            return {
              chars: acc2.chars + content.length,
              words: acc2.words + numWords,
              lines: acc2.lines + Math.ceil(numWords / config.wordsPerLine),
            }
          } else if (!ignoreContent && isEmpty === true) {
            return { ...acc2, lines: acc2.lines + 1 }
          }
        },
        { words: 0, lines: 0, chars: 0 }
      )
      return {
        chars: acc.chars + chars,
        words: acc.words + words,
        inches: (acc.lines + lines) / config.linesPerInch,
        lines: acc.lines + lines,
      }
    },
    { words: 0, lines: 0, chars: 0, inches: 0 }
  )
}
